import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Wallet, X } from "lucide-react";
import api from "../lib/api";

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function BudgetDialog({ cycle, onClose, onSaved }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (cycle) setValue(cycle.budget ? String(cycle.budget) : ""); }, [cycle]);
  if (!cycle) return null;

  const save = async (budget) => {
    setBusy(true);
    try {
      await api.patch(`/api/live-cycles/${cycle.id}/budget`, { budget });
      toast.success(budget ? "Orçamento do ciclo salvo." : "Orçamento removido.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar orçamento.");
    } finally {
      setBusy(false);
    }
  };
  const parsed = parseFloat(String(value).replace(",", "."));

  return (
    <div data-testid="budget-dialog-overlay" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-extrabold text-base">Orçamento do ciclo #{cycle.cycle_number}</h3>
          <button data-testid="budget-dialog-close-btn" onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <label className="block text-[11px] font-bold text-gray-300 mb-1">Valor total dedicado (R$)</label>
        <input data-testid="budget-dialog-input" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="500,00"
          className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-2 text-white text-sm focus:outline-none focus:border-[#FF6B00]" />
        <p className="text-[11px] text-gray-500 mt-2">Já investido neste ciclo: <strong className="text-gray-300">{fmtBRL(cycle.total_spend)}</strong>. O Conselheiro usa este valor nas sugestões.</p>
        <div className="flex gap-2 mt-4">
          {cycle.budget && (
            <button data-testid="budget-dialog-remove-btn" disabled={busy} onClick={() => save(null)} className="py-2 px-3 rounded-lg bg-[#1F2330] border border-[#3E4760] text-gray-300 font-bold text-xs uppercase">Remover</button>
          )}
          <button data-testid="budget-dialog-save-btn" disabled={busy || !(parsed > 0)} onClick={() => save(parsed)}
            className="flex-1 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Salvar orçamento"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CycleBudgetBar({ cycle, onEdit, onChanged }) {
  const n = cycle.cycle_number;
  if (!cycle.budget) {
    return (
      <button data-testid={`cycle-budget-declare-${n}`} onClick={onEdit} className="mt-2 text-[11px] text-[#FF6B00] hover:underline flex items-center gap-1">
        <Wallet className="w-3 h-3" /> Declarar orçamento do ciclo
      </button>
    );
  }
  const pct = cycle.budget_used_pct || 0;
  const tone = cycle.over_budget ? "bg-red-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-400";

  const ignore = async () => {
    try {
      await api.patch(`/api/live-cycles/${cycle.id}/budget`, { ignore_overrun: true });
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao atualizar.");
    }
  };

  return (
    <div data-testid={`cycle-budget-${n}`} className="mt-2">
      <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1">
        <button data-testid={`cycle-budget-edit-${n}`} onClick={onEdit} className="flex items-center gap-1 hover:text-white">
          <Wallet className="w-3 h-3 text-[#FF6B00]" />
          {fmtBRL(cycle.total_spend)} de {fmtBRL(cycle.budget)}
        </button>
        <span data-testid={`cycle-budget-pct-${n}`} className={cycle.over_budget ? "text-red-400 font-bold" : ""}>
          {pct}% · {cycle.over_budget ? `estourou ${fmtBRL(-cycle.budget_remaining)}` : `resta ${fmtBRL(cycle.budget_remaining)}`}
        </span>
      </div>
      <div className="h-1.5 bg-[#1A1D26] rounded-full overflow-hidden">
        <div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {cycle.over_budget && !cycle.budget_overrun_ignored && (
        <div data-testid={`cycle-budget-overrun-alert-${n}`} className="mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/40 text-[11px] text-red-200">
          <div className="flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>O gasto ultrapassou o orçamento declarado deste ciclo.</span>
          </div>
          <div className="flex gap-2 mt-2">
            <button data-testid={`cycle-budget-new-${n}`} onClick={onEdit} className="px-2 py-1 rounded-md bg-[#FF6B00] text-black font-extrabold uppercase text-[10px]">Declarar novo orçamento</button>
            <button data-testid={`cycle-budget-ignore-${n}`} onClick={ignore} className="px-2 py-1 rounded-md bg-[#1F2330] border border-[#3E4760] text-gray-300 font-bold uppercase text-[10px]">Ignorar</button>
          </div>
        </div>
      )}
    </div>
  );
}
